<script lang="ts">
import type { InputNumberProps, InputNumberSlots } from '#ui/types';
import { Decimal as DecimalBase } from 'decimal.js-light';

/**
 * decimal.js-light rounds every arithmetic result to a global 20 significant digits, which
 * silently truncates columns wider than that (Postgres `numeric` allows up to 1000). Step on an
 * isolated constructor that never rounds rather than reconfiguring the shared one.
 */
const Decimal = DecimalBase.clone({ precision: 1e9 });
type Decimal = DecimalBase;

export interface InputDecimalNumberProps
	extends Pick<
		InputNumberProps,
		| 'placeholder'
		| 'color'
		| 'variant'
		| 'size'
		| 'highlight'
		| 'orientation'
		| 'increment'
		| 'incrementIcon'
		| 'incrementDisabled'
		| 'decrement'
		| 'decrementIcon'
		| 'decrementDisabled'
		| 'disabled'
		| 'readonly'
		| 'required'
		| 'id'
		| 'name'
		| 'class'
		| 'ui'
		| 'autofocus'
		| 'autofocusDelay'
	> {
	/**
	 * Input may receive stale field values with another runtime type. Invalid
	 * values are discarded before reaching decimal parsing.
	 */
	modelValue?: unknown;
	/** Default value when modelValue is undefined */
	defaultValue?: string;
	/** Minimum value (as string for arbitrary precision) */
	min?: string;
	/** Maximum value (as string for arbitrary precision) */
	max?: string;
	/** Step value (as string for arbitrary precision) */
	step?: string;
	/** Total number of significant digits allowed (not counting minus sign) */
	precision?: number;
	/** Number of decimal places allowed */
	scale?: number;
}

export interface InputDecimalNumberEmits {
	'update:modelValue': [value: string | null];
	'blur': [event: FocusEvent];
	'change': [event: Event];
}

export type InputDecimalNumberSlots = InputNumberSlots;
</script>

<script setup lang="ts">
import { createReusableTemplate } from '@vueuse/core';
import { tv } from 'tailwind-variants';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useAppConfig } from '#app/config';
import inputNumber from '#build/ui/input-number';
import { useMergedTheme } from '#layers/ui/composables/use-merged-theme';
import { useFieldGroup } from '#ui/composables/useFieldGroup';
import { useFormField } from '#ui/composables/useFormField';

const props = withDefaults(defineProps<InputDecimalNumberProps>(), {
	step: '1',
	orientation: 'vertical',
	increment: true,
	decrement: true,
});

const emit = defineEmits<InputDecimalNumberEmits>();

defineSlots<InputDecimalNumberSlots>();

const appConfig = useAppConfig();
const theme = useMergedTheme({ inputNumber });

const {
	id,
	name,
	disabled,
	size: formGroupSize,
	emitFormBlur,
	emitFormChange,
	emitFormInput,
} = useFormField<InputDecimalNumberProps>(props);
const { size: fieldGroupSize, orientation: fieldGroupOrientation } = useFieldGroup<InputDecimalNumberProps>(props);

const localValue = ref<string>('');
const isTouched = ref(false);
const isFocused = ref(false);
const inputRef = useTemplateRef<HTMLInputElement>('inputRef');

defineExpose({
	inputRef: computed(() => inputRef.value),
});

const minDecimal = computed(() => {
	if (props.min === undefined) return null;

	try {
		return new Decimal(props.min);
	}
	catch {
		return null;
	}
});

const maxDecimal = computed(() => {
	if (props.max === undefined) return null;

	try {
		return new Decimal(props.max);
	}
	catch {
		return null;
	}
});

const stepDecimal = computed(() => {
	try {
		return new Decimal(props.step ?? '1');
	}
	catch {
		return new Decimal(1);
	}
});

const inputSize = computed(() => fieldGroupSize.value || formGroupSize.value);

const incrementIcon = computed(
	() => props.incrementIcon || (props.orientation === 'horizontal' ? appConfig.ui.icons.plus : appConfig.ui.icons.chevronUp),
);
const decrementIcon = computed(
	() => props.decrementIcon || (props.orientation === 'horizontal' ? appConfig.ui.icons.minus : appConfig.ui.icons.chevronDown),
);

// A read-only field drops the steppers rather than showing a control that refuses to act; that
// also releases the padding the theme reserves for them.
const showIncrement = computed(() => !!props.increment && !props.readonly);
const showDecrement = computed(() => !!props.decrement && !props.readonly);

const ui = computed(() =>
	tv({ extend: tv(theme) })({
		color: props.color,
		variant: props.variant,
		size: inputSize.value,
		highlight: props.highlight,
		orientation: props.orientation,
		fieldGroup: fieldGroupOrientation.value,
		increment: props.orientation === 'vertical' ? showIncrement.value || showDecrement.value : showIncrement.value,
		decrement: props.orientation === 'vertical' ? false : showDecrement.value,
		disabled: disabled.value,
	}),
);

function initLocalValue() {
	if (props.modelValue === null || props.modelValue === undefined) {
		localValue.value = props.defaultValue ?? '';
	}
	else if (typeof props.modelValue !== 'string') {
		// Non-string values (e.g. boolean from a stale field type) are discarded — they're invalid for a decimal input
		localValue.value = '';
	}
	else {
		localValue.value = props.modelValue;
	}
}

watch(
	() => props.modelValue,
	() => {
		if (!isFocused.value) {
			initLocalValue();
		}
	},
	{ immediate: true },
);

function normalizeDecimalSeparator(value: string): string {
	return value.replaceAll(',', '.');
}

function isPartiallyValid(value: string): boolean {
	if (value === '' || value === '-') return true;

	const hasLeadingMinus = value.startsWith('-');
	const numericPart = hasLeadingMinus ? value.slice(1) : value;

	const decimalCount = (numericPart.match(/\./g) || []).length;
	if (decimalCount > 1) return false;

	if (!/^\d*(?:\.\d*)?$/.test(numericPart)) return false;

	// (minus doesn't count toward precision)
	if (props.precision !== undefined) {
		const digits = numericPart.replace('.', '');
		if (digits.length > props.precision) return false;
	}

	if (props.scale !== undefined) {
		const decimalIndex = numericPart.indexOf('.');

		if (decimalIndex !== -1) {
			const decimalPlaces = numericPart.length - decimalIndex - 1;
			if (decimalPlaces > props.scale) return false;
		}
	}

	return true;
}

function isFullyValid(value: string): boolean {
	if (value === '') return true;
	if (value === '-') return false;
	if (!isPartiallyValid(value)) return false;

	try {
		const decimal = new Decimal(value);
		if (minDecimal.value && decimal.lt(minDecimal.value)) return false;
		if (maxDecimal.value && decimal.gt(maxDecimal.value)) return false;
		return true;
	}
	catch {
		return false;
	}
}

function clamp(value: string): string {
	if (value === '' || value === '-') return value;

	try {
		let decimal = new Decimal(value);
		if (minDecimal.value && decimal.lt(minDecimal.value)) decimal = minDecimal.value;
		if (maxDecimal.value && decimal.gt(maxDecimal.value)) decimal = maxDecimal.value;
		return decimal.toString();
	}
	catch {
		return value;
	}
}

function stepValue(direction: 1 | -1, multiplier: number = 1) {
	if (disabled.value || props.readonly) return;

	let currentValue: Decimal;

	if (localValue.value === '' || localValue.value === '-') {
		currentValue = new Decimal(0);
	}
	else {
		try {
			currentValue = new Decimal(localValue.value);
		}
		catch {
			return;
		}
	}

	const step = stepDecimal.value.times(multiplier).times(direction);
	let newValue = currentValue.plus(step);

	if (minDecimal.value && newValue.lt(minDecimal.value)) newValue = minDecimal.value;
	if (maxDecimal.value && newValue.gt(maxDecimal.value)) newValue = maxDecimal.value;

	// Scale caps the decimal places, it doesn't pad them: stepping 12.5 must not yield 12.50.
	const result = (props.scale !== undefined ? newValue.toDecimalPlaces(props.scale) : newValue).toString();
	localValue.value = result;
	applyValue(result);
}

function applyValue(value: string) {
	const normalizedValue = value === '' ? null : value;
	emit('update:modelValue', normalizedValue);
	emitFormChange();
	emitFormInput();
}

function onBeforeInput(event: InputEvent) {
	if (props.readonly || disabled.value) {
		event.preventDefault();
		return;
	}

	const input = event.target as HTMLInputElement;
	const data = event.data;

	if (!data) return;

	const normalizedData = normalizeDecimalSeparator(data);

	const selectionStart = input.selectionStart ?? 0;
	const selectionEnd = input.selectionEnd ?? 0;
	const currentValue = input.value;
	const prospectiveValue = currentValue.slice(0, selectionStart) + normalizedData + currentValue.slice(selectionEnd);

	if (!isPartiallyValid(prospectiveValue)) {
		event.preventDefault();
		return;
	}

	if (/e/i.test(normalizedData)) {
		event.preventDefault();
		return;
	}

	if (normalizedData.includes('-') && (selectionStart !== 0 || currentValue.includes('-'))) {
		event.preventDefault();
	}
}

// Handle input event (fallback for paste/IME)
function onInput(event: Event) {
	const input = event.target as HTMLInputElement;
	const value = normalizeDecimalSeparator(input.value);

	if (!isPartiallyValid(value)) {
		input.value = localValue.value;
		return;
	}

	localValue.value = value;
	isTouched.value = true;
}

function onFocus() {
	isFocused.value = true;
}

function onBlur(event: FocusEvent) {
	isFocused.value = false;
	isTouched.value = true;

	let value = localValue.value;

	if (value === '-' || value === '-.') {
		value = '';
		localValue.value = value;
	}

	if (value.endsWith('.')) {
		value = value.slice(0, -1);
		localValue.value = value;
	}

	const clamped = clamp(value);

	if (clamped !== value) {
		localValue.value = clamped;
		value = clamped;
	}

	applyValue(value);
	emit('blur', event);
	emitFormBlur();
}

function onChange(event: Event) {
	emit('change', event);
	emitFormChange();
}

function onKeyDown(event: KeyboardEvent) {
	if (disabled.value || props.readonly) return;

	switch (event.key) {
		case 'ArrowUp':
			event.preventDefault();
			stepValue(1);
			break;
		case 'ArrowDown':
			event.preventDefault();
			stepValue(-1);
			break;
		case 'PageUp':
			event.preventDefault();
			stepValue(1, 10);
			break;
		case 'PageDown':
			event.preventDefault();
			stepValue(-1, 10);
			break;
		case 'Home':
			if (props.min !== undefined) {
				event.preventDefault();
				localValue.value = props.min;
				applyValue(props.min);
			}

			break;
		case 'End':
			if (props.max !== undefined) {
				event.preventDefault();
				localValue.value = props.max;
				applyValue(props.max);
			}

			break;
		case 'Enter':
			event.preventDefault();

			if (isFullyValid(localValue.value)) {
				const clamped = clamp(localValue.value);
				localValue.value = clamped;
				applyValue(clamped);
			}

			break;
	}
}

// Exact plain notation: a JS number would round values beyond double precision, and `toString()`
// switches to exponent notation for large magnitudes. Browsers still expose `aria-valuenow` to
// assistive tech as a double, so the same text also goes out as `aria-valuetext`, which is announced verbatim.
function toAriaValue(value: string): string | undefined {
	if (value === '' || value === '-') return undefined;

	try {
		return new Decimal(value).toFixed();
	}
	catch {
		return undefined;
	}
}

const ariaValueNow = computed(() => toAriaValue(localValue.value));
const ariaValueMin = computed(() => props.min === undefined ? undefined : toAriaValue(props.min));
const ariaValueMax = computed(() => props.max === undefined ? undefined : toAriaValue(props.max));

const ariaInvalid = computed<'true' | undefined>(() => {
	if (!isTouched.value) return;
	return !isFullyValid(localValue.value) ? 'true' : undefined;
});

const isIncrementDisabled = computed(() => {
	if (disabled.value || props.readonly || props.incrementDisabled) return true;
	if (!maxDecimal.value) return false;
	if (localValue.value === '' || localValue.value === '-') return false;

	try {
		return new Decimal(localValue.value).gte(maxDecimal.value);
	}
	catch {
		return false;
	}
});

const isDecrementDisabled = computed(() => {
	if (disabled.value || props.readonly || props.decrementDisabled) return true;
	if (!minDecimal.value) return false;
	if (localValue.value === '' || localValue.value === '-') return false;

	try {
		return new Decimal(localValue.value).lte(minDecimal.value);
	}
	catch {
		return false;
	}
});

function autoFocus() {
	if (props.autofocus) {
		inputRef.value?.focus();
	}
}

onMounted(() => {
	setTimeout(() => {
		autoFocus();
	}, props.autofocusDelay);
});

const [DefineStepButton, ReuseStepButton] = createReusableTemplate<{
	'direction': 1 | -1;
	'icon': string;
	'disabled': boolean;
	'aria-label': string;
}>();
</script>

<template>
	<DefineStepButton v-slot="{ direction, icon, disabled: isDisabled, ...attrs }">
		<DButton
			:icon
			:color="color ?? 'neutral'"
			:size="inputSize"
			:disabled="isDisabled"
			:aria-label="attrs['aria-label']"
			tabindex="-1"
			variant="link"
			class="text-dimmed"
			@click="stepValue(direction)"
		/>
	</DefineStepButton>

	<div
		role="group"
		:class="ui.root({ class: [props.ui?.root, props.class] })"
		v-bind="$attrs"
	>
		<input
			:id
			ref="inputRef"
			type="text"
			inputmode="decimal"
			role="spinbutton"
			aria-roledescription="Number field"
			:name
			:placeholder
			:disabled
			:readonly
			:required
			:aria-valuenow="ariaValueNow"
			:aria-valuetext="ariaValueNow"
			:aria-valuemin="ariaValueMin"
			:aria-valuemax="ariaValueMax"
			:aria-invalid
			:value="localValue"
			:class="ui.base({ class: props.ui?.base })"
			@beforeinput="onBeforeInput"
			@input="onInput"
			@focus="onFocus"
			@blur="onBlur"
			@change="onChange"
			@keydown="onKeyDown"
		>

		<div
			v-if="showDecrement && orientation === 'horizontal'"
			:class="ui.decrement({ class: props.ui?.decrement })"
		>
			<slot name="decrement">
				<ReuseStepButton :direction="-1" :icon="decrementIcon" :disabled="isDecrementDisabled" :aria-label="$t('common.decrement')" />
			</slot>
		</div>

		<div
			v-if="showIncrement"
			:class="ui.increment({ class: props.ui?.increment })"
		>
			<slot name="increment">
				<ReuseStepButton :direction="1" :icon="incrementIcon" :disabled="isIncrementDisabled" :aria-label="$t('common.increment')" />
			</slot>
		</div>

		<div
			v-if="showDecrement && orientation === 'vertical'"
			:class="ui.decrement({ class: props.ui?.decrement })"
		>
			<slot name="decrement">
				<ReuseStepButton :direction="-1" :icon="decrementIcon" :disabled="isDecrementDisabled" :aria-label="$t('common.decrement')" />
			</slot>
		</div>
	</div>
</template>
